// WALLET-P1: small parts the client and operator Billing screens share — a control shown
// disabled with the reason in a tooltip (never hidden), a row action disabled the same way, and
// the wallet status chip (OK / low / paused / off).
import { Badge, Button, cn, Tooltip, TooltipContent, TooltipTrigger } from "@companyos/ui";
import type { ComponentType, ReactNode } from "react";

/** A disabled control with the reason on hover or focus (the span takes the pointer). */
export function DisabledWhy({
  why,
  children,
  className,
}: {
  why: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={cn("inline-flex", className)} />}>
        {children}
      </TooltipTrigger>
      <TooltipContent>{why}</TooltipContent>
    </Tooltip>
  );
}

type IconType = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

/** A row's icon action that is not allowed here: visible, disabled, the reason as its tooltip. */
export function DisabledRowAction({
  icon: Icon,
  label,
  why,
}: {
  icon: IconType;
  label: string;
  why: string;
}) {
  return (
    <DisabledWhy why={why}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`${label} (${why})`}
        disabled
        className="text-muted-foreground"
      >
        <Icon aria-hidden={true} />
      </Button>
    </DisabledWhy>
  );
}

export type WalletStatus = "ok" | "low" | "paused" | "off";

const STATUS: Record<
  WalletStatus,
  { label: string; tone: "success" | "warning" | "danger" | "neutral" }
> = {
  ok: { label: "OK", tone: "success" },
  low: { label: "Low", tone: "warning" },
  paused: { label: "Paused", tone: "danger" },
  off: { label: "Off", tone: "neutral" },
};

export function WalletStatusChip({
  status,
  className,
}: {
  status: WalletStatus;
  className?: string;
}) {
  const s = STATUS[status];
  return (
    <Badge variant={s.tone} dot data-wallet-status={status} className={className}>
      {s.label}
    </Badge>
  );
}
